import { EMPTY_DATA, getConsentStatusString } from "../../../constants/consent";
import { getLastCommaFirstName } from "../../../util/common";
import { deepJSONParse } from "../../../util/deepJSONParse";
import type { ITimeProvider } from "../driven/providers/provider";

export const PatientControllerFactory = ({
  patientService,
  timeProvider,
  cdlService,
}: {
  patientService: IPatientService;
  timeProvider: ITimeProvider;
  cdlService: ICDLService;
}) => {
  const getPatientSummaryProfile: IGetPatientSummaryProfile = async ({
    clinicId,
    patientId,
  }) => {
    const summary = await patientService.getPatientSummary({
      clinicId,
      patientId,
    });
    if (summary.data?.data) {
      const { resident, admission, location, alert_detail } = summary.data.data;
      const age = timeProvider
        .dateLibrary(timeProvider.dateLibrary())
        .diff(resident.dob, "years");

      return {
        success: true,
        data: {
          summary: {
            residentId: resident.id,
            hasAdmission: admission != null,
            admissionId: admission?.id ?? null,
            firstName: resident.first_name ?? "",
            lastName: resident.last_name ?? "",
            name: getLastCommaFirstName(
              resident.first_name,
              resident.last_name
            ),
            age: isNaN(age) ? null : age,
            gender: resident.gender || "",
            consentedStatus:
              admission?.consented_status != null
                ? getConsentStatusString(admission.consented_status)
                : "",
            doa: admission?.date_of_admission
              ? timeProvider
                  .dateLibrary(admission.date_of_admission, "YYYY-MM-DD")
                  ?.format("MM-DD-YYYY")
              : "",
            dob: resident.dob
              ? timeProvider
                  .dateLibrary(resident.dob, "YYYY-MM-DD")
                  ?.format("MM-DD-YYYY")
              : "-",
            level: alert_detail ? alert_detail.level : null,
            room: location.room,
            floorId: location.floor_id,
            date_of_discharge: admission?.date_of_discharge
              ? timeProvider
                  .dateLibrary(admission.date_of_discharge, "YYYY-MM-DD")
                  ?.format("MM-DD-YYYY")
              : null,
            payer_information_json: admission?.payer_information_json ?? null,
            extra_info: admission?.extra_info ?? null,
            deviceId: location.device_id ?? null,
          },
        },
      };
    }
    return {
      success: false,
    };
  };

  const getPatientBillingInfo: IGetPatientBillingInfo = async ({
    clinicId,
    patientId,
  }) => {
    const result = await patientService.getPatientListV2({
      clinicId,
      patientId,
    });

    if (!result.success || !result.data || result.data.length === 0) {
      return {
        success: false,
        error: { message: result.error?.message ?? "No patient data found" },
      };
    }

    const item = result.data[0];

    // payer_information_json / extra_info arrive as parsed objects here but the
    // payer-rule scanner (and the v4 chart-summary contract) expect a string —
    // stringify objects/arrays, pass strings through untouched.
    const toJsonString = (value: unknown): string | null => {
      if (value == null) return null;
      if (typeof value === "string") return value;
      try {
        return JSON.stringify(value);
      } catch {
        return null;
      }
    };

    // `diagnosis` is usually a JSON-stringified array but some responses
    // return it already parsed — handle both.
    let rawDiagnosis: unknown = item.diagnosis;
    if (typeof rawDiagnosis === "string" && rawDiagnosis.trim()) {
      try {
        rawDiagnosis = JSON.parse(rawDiagnosis);
      } catch {
        rawDiagnosis = [];
      }
    }

    const savedDiagnoses: IDiagnosisEntry[] = Array.isArray(rawDiagnosis)
      ? rawDiagnosis
          .filter(
            (d): d is Record<string, unknown> => !!d && typeof d === "object"
          )
          .map((d) => ({
            icd10: String(d.icd10 ?? ""),
            icd10Description: String(d.icd10Description ?? ""),
            rank: Number(d.rank ?? 0),
            conditionId:
              typeof d.conditionId === "number" ? d.conditionId : undefined,
          }))
          .filter((d) => d.rank > 0 && !!d.icd10)
      : [];

    // Age from `dob` (same calc as getPatientSummaryProfile).
    let age: number | null = null;
    if (typeof item.dob === "string" && item.dob) {
      const computed = timeProvider
        .dateLibrary(timeProvider.dateLibrary())
        .diff(item.dob, "years");
      age = isNaN(computed) ? null : computed;
    }

    // Gender lives inside `extra_info` (object or stringified).
    let gender: string | null = null;
    const extraInfoObj: unknown =
      typeof item.extra_info === "string"
        ? (() => {
            try {
              return JSON.parse(item.extra_info);
            } catch {
              return null;
            }
          })()
        : item.extra_info;
    if (extraInfoObj && typeof extraInfoObj === "object") {
      const g = (extraInfoObj as Record<string, unknown>).gender;
      if (typeof g === "string" && g.trim()) gender = g;
    }

    return {
      success: true,
      data: {
        payerInformationJson: toJsonString(item.payer_information_json),
        extraInfo: toJsonString(item.extra_info),
        savedDiagnoses,
        age,
        gender,
      },
    };
  };

  const getPatientPanelProfile: IGetPatientPanelProfile = async ({
    clinicId,
    patientId,
  }) => {
    if (!patientId) {
      // Just for safe side this should not be called when used with useSWR
      throw new Error("Patient Id null");
    }
    const summary = await patientService.getPatientSummary({
      clinicId,
      patientId,
    });
    // A non-2xx comes back as the error body (``{ detail: { message } }``,
    // e.g. "Resident or room not found" when the resident isn't at this
    // clinic) rather than throwing; report it instead of crashing below.
    const errorBody = summary.data as
      | { data?: unknown; detail?: { message?: unknown } | string }
      | undefined;
    if (errorBody && !errorBody.data) {
      const detail = errorBody.detail;
      return {
        success: false,
        error: {
          message:
            typeof detail === "string"
              ? detail
              : typeof detail?.message === "string"
              ? detail.message
              : "Patient summary unavailable",
        },
      };
    }

    let data = {};
    if (summary.data) {
      const { resident, admission, alert_detail, location, device } =
        summary.data.data;
      const age = timeProvider
        .dateLibrary(timeProvider.dateLibrary())
        .diff(resident.dob, "years");

      // Parse and format DOB
      const dobDateObject = resident.dob
        ? timeProvider.dateLibrary(resident.dob, "YYYY-MM-DD")
        : null;
      const formattedDob = dobDateObject?.format("MM-DD-YYYY") || "-";

      // Compassus patients in pending status (status 5) can exist before an
      // admission record is created. Their resident identity is still valid
      // and should render independently of admission data.
      const doaDateObject = admission?.date_of_admission
        ? timeProvider.dateLibrary(admission.date_of_admission, "YYYY-MM-DD")
        : null;
      const formattedDoa = doaDateObject?.format("MM-DD-YYYY") || "";

      data = {
        ...data,
        device: device ?? null,
        summary: {
          residentId: resident.id,
          hasAdmission: admission != null,
          admissionId: admission?.id ?? null,
          firstName: resident.first_name ?? "",
          lastName: resident.last_name ?? "",
          name: getLastCommaFirstName(resident.first_name, resident.last_name),
          age: isNaN(age) ? "" : age,
          gender: resident.gender || "",
          consentedStatus:
            admission?.consented_status != null
              ? getConsentStatusString(admission.consented_status)
              : "",
          doa: formattedDoa,
          dob: formattedDob,
          level: alert_detail ? alert_detail.level : null,
          room: location?.room ?? "",
          floorId: location?.floor_id,
          date_of_discharge: admission?.date_of_discharge
            ? timeProvider
                .dateLibrary(admission.date_of_discharge, "YYYY-MM-DD")
                ?.format("MM-DD-YYYY")
            : null,
          hospice_status: resident.hospice_status,
          pr_ins_name: resident.pr_ins_name,
          payer_information_json: admission?.payer_information_json ?? null,
          alert_flags_muted_until:
            resident.settings?.flag_preferences?.muted_until ?? null,
        },
      };

      if (admission && admission.consented_status !== 0) {
        data = {
          ...data,
          circadiaVital: {
            rr: {
              value: EMPTY_DATA.NA,
              date: "",
              hasReachedThreshold: false,
            },
            heart_rate: {
              value: EMPTY_DATA.NA,
              date: "",
              hasReachedThreshold: false,
            },
            motion: {
              value: EMPTY_DATA.NA,
              date: "",
              hasReachedThreshold: false,
            },
            date: "",
          },
        };

        data = {
          ...data,
          pccVital: {
            bloodSugar: {
              value: EMPTY_DATA.NA,
              time: EMPTY_DATA.NDA,
              hasReachedThreshold: false,
            },
            bloodPressure: {
              systolic: {
                value: EMPTY_DATA.NA,
                hasReachedThreshold: false,
              },
              diastolic: {
                value: EMPTY_DATA.NA,
                hasReachedThreshold: false,
              },
              time: EMPTY_DATA.NDA,
            },
            oxygenSaturation: {
              value: EMPTY_DATA.NA,
              time: EMPTY_DATA.NDA,
              hasReachedThreshold: false,
            },
            heartrate: {
              value: EMPTY_DATA.NA,
              time: EMPTY_DATA.NDA,
              hasReachedThreshold: false,
            },
            temperature: {
              value: EMPTY_DATA.NA,
              time: EMPTY_DATA.NDA,
              hasReachedThreshold: false,
            },
            weight: {
              value: EMPTY_DATA.NA,
              time: EMPTY_DATA.NDA,
              hasReachedThreshold: false,
            },
          },
        };
      }
    }

    return {
      success: true,
      data,
    };
  };

  function generateDateRange(
    startDateString: string,
    endDateString: string
  ): string[] {
    const startDate = timeProvider.dateLibrary(startDateString);
    const endDate = timeProvider.dateLibrary(endDateString);
    const dateRange: string[] = [];

    if (endDate.isBefore(startDate)) {
      throw new Error("End date must be greater than or equal to start date.");
    }

    let currentDate = startDate;
    while (currentDate.isSame(endDate) || currentDate.isBefore(endDate)) {
      dateRange.push(currentDate.format("YYYY-MM-DD 00:00:00"));
      currentDate = currentDate.add(1, "day");
    }

    return dateRange;
  }

  function generateHourlyDateRange(
    startDateString: string,
    endDateString: string
  ): string[] {
    const startDateTime = timeProvider.dateLibrary(startDateString);
    const endDateTime = timeProvider.dateLibrary(endDateString);
    const hourlyDateRange: string[] = [];

    if (endDateTime.isBefore(startDateTime)) {
      throw new Error("End date must be greater than or equal to start date.");
    }

    let currentDateTime = startDateTime;
    while (
      currentDateTime.isSame(endDateTime) ||
      currentDateTime.isBefore(endDateTime)
    ) {
      hourlyDateRange.push(currentDateTime.format("YYYY-MM-DD HH:mm:ss"));
      currentDateTime = currentDateTime.add(1, "hour");
    }

    return hourlyDateRange;
  }

  const getCircadiaIQ: IGetCircadiaIQController = async ({
    clinicId,
    patientId,
    shouldCall = true,
    type,
    date,
  }) => {
    if (!shouldCall) {
      return {
        error: { message: "" },
        success: false,
      };
    }

    const payload: Parameters<typeof patientService.getCircadiaIQ>[0] = {
      clinic_id: clinicId,
      patient_id: patientId,
      type,
    };

    if (date) {
      payload.date = date;
    }

    const circadiaIQData = await patientService.getCircadiaIQ(payload);

    const currentStatus = circadiaIQData.data?.data.latest_data_status;
    const status = [1, 2, 3, 4];

    if (currentStatus !== undefined && status.includes(currentStatus)) {
      if (circadiaIQData.data?.data.data !== null) {
        const dataToReturn = circadiaIQData.data
          ? (deepJSONParse(
              circadiaIQData.data!.data.data.response
            ) as unknown as {
              Insights?: string[];
              "Clinical Pathway"?: string[];
              situation?: string[];
              background?: string[];
              assessment?: {
                most_likely_conditions: string[];
                conditions_not_to_miss: string[];
                new_md_orders?: string[];
                md_np_pa_communication?: string[];
                type_of_pending_lab?: string[];
                type_of_new_or_upcoming_labs?: string[];
                medications_administered_or_new?: string[];
              };
              recommendations?: string[];
            })
          : {
              Insights: [],
              "Clinical Pathway": [],
              situation: [],
              background: [],
              assessment: {
                most_likely_conditions: [],
                conditions_not_to_miss: [],
                new_md_orders: [],
                md_np_pa_communication: [],
                type_of_pending_lab: [],
                type_of_new_or_upcoming_labs: [],
                medications_administered_or_new: [],
              },
              recommendations: [],
            };
        return {
          success: true,
          data: {
            ...circadiaIQData.data!.data,
            response: dataToReturn as unknown as {
              Insights?: string[];
              "Clinical Pathway"?: string[];
              situation?: string[];
              background?: string[];
              assessment?: {
                most_likely_conditions: string[];
                conditions_not_to_miss: string[];
              };
              recommendations?: string[];
            },
          },
        };
      }
      return {
        success: true,
        data: {
          ...circadiaIQData.data!.data,
        },
      };
    }
    return {
      error: { message: "" },
      success: false,
    };
  };

  const generateCircadiaIQ: IGenerateCircadiaIQController = async ({
    clinicId,
    patientId,
    type,
    date,
  }) => {
    const generateCircadiaIQ = await patientService.generateCircadiaIQ({
      clinic_id: clinicId,
      patient_id: patientId,
      type,
      date,
    });
    if (generateCircadiaIQ.success && generateCircadiaIQ.data) {
      return { data: generateCircadiaIQ.data, success: true };
    }

    return { error: { message: "" }, success: false };
  };

  const getOrderIQ: IGetOrderIQController = async ({ clinicId, patientId }) => {
    const response = await patientService.getOrderIQ({
      clinic_id: clinicId,
      patient_id: patientId,
    });

    if (!response.success || !response.data) {
      return {
        success: false,
        error: { message: response.error?.message ?? "" },
      };
    }

    const inner = response.data.data?.data ?? null;
    return {
      success: true,
      data: {
        medicalNecessitySummary:
          inner?.response?.medical_necessity_summary ?? null,
        recommendedDiagnoses: inner?.response?.recommended_diagnoses ?? [],
        generatedAt: inner?.generated_at ?? inner?.created_at ?? null,
        status: inner?.status ?? null,
        latestDataStatus: response.data.data?.latest_data_status ?? null,
      },
    };
  };

  const getCircadiaIQForRecertification: IGetCircadiaIQForRecertificationController =
    async ({ clinicId, patientId, shouldCall = true }) => {
      if (!shouldCall) {
        return { success: false, error: { message: "" } };
      }

      const response = await patientService.getCircadiaIQForRecertification({
        clinic_id: clinicId,
        patient_id: patientId,
      });

      if (!response.success || !response.data) {
        return {
          success: false,
          error: { message: response.error?.message ?? "" },
        };
      }

      // Mirror the Web Portal saga (GET_CIRCADIA_IQ_FOR_RECERTIFICATION) and
      // the sibling `getCircadiaIQ` here: only surface the narrative when the
      // cron job reports a "ready" status. Any other status (pending/failed)
      // is treated as "no note", matching the portal which returns success:
      // false in that case so the assessment editor keeps its default seed.
      const currentStatus = response.data.data?.latest_data_status;
      const readyStatuses = [1, 2, 3, 4];
      if (
        currentStatus === undefined ||
        !readyStatuses.includes(currentStatus)
      ) {
        return { success: false, error: { message: "" } };
      }

      // The cron endpoint may return `data: null` when no narrative has ever
      // been generated for the patient. `data.response` is already a parsed
      // object in CIP (see patientRespository.d.ts), unlike the portal where
      // it arrives stringified — so read `recertification_note` directly.
      const inner = response.data.data?.data ?? null;
      return {
        success: true,
        data: {
          recertificationNote: inner?.response?.recertification_note ?? null,
          generatedAt: inner?.generated_at ?? inner?.created_at ?? null,
          status: inner?.status ?? null,
          latestDataStatus: currentStatus ?? null,
        },
      };
    };

  const getPatientVitalAndRRGraph: IGetPatientVitalAndRRGraph = async ({
    clinicId,
    patientId,
    typeOfRange,
    isInitialLoaded,
    shouldCall = true,
  }) => {
    if (!shouldCall) {
      return { success: false, error: { message: "" } };
    }
    if (!patientId) {
      // Just for safe side this should not be called when used with useSWR
      throw new Error("Patient Id null");
    }

    let dateRange = {
      to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
      from: timeProvider
        .getDateTime()
        .subtract(7, "days")
        .format("YYYY-MM-DDT00:00:00Z"),
      toForRangeGenerator: timeProvider
        .getDateTime()
        .format("YYYY-MM-DDTHH:mm:ss"),
      fromforRangeGenerator: timeProvider
        .getDateTime()
        .subtract(7, "days")
        .format("YYYY-MM-DDT00:00:00"),
    };

    switch (typeOfRange) {
      case "4H":
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(4, "hours")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(4, "hours")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;
      case "1D":
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(1, "days")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(1, "days")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;
      case "3D":
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(3, "days")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(3, "days")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;

      case "5D":
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(5, "days")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(5, "days")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;
      case "30D":
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(30, "days")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(30, "days")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;
      default:
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(7, "days")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(7, "days")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;
    }

    let endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD HH:mm:ss");

    if (["30D"].includes(typeOfRange)) {
      endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD 23:59:59");
    }

    // Add converter here
    const [vital, RR, motionBedExitData, heartRateData] = await Promise.all([
      patientService.getPatientVitalGraph({
        clinicId,
        patientId,
        endDate,
        typeOfRange,
      }),

      getPatientRRGraphData({
        patientId,
        isInitialLoaded,
      }),
      getPatientMotionBedExitData({
        patientId,
        dateRange,
        isInitialLoaded,
      }),
      getPatientHeartRateData({ patientId, isInitialLoaded }),
    ]);

    if (vital.data && vital.data.data) {
      const vitalData = vital.data.data;
      let dateRangeArray: string[] = [];
      let formatToCompare = "YYYY-MM-DD";
      if (
        typeOfRange === "1D" ||
        typeOfRange === "3D" ||
        typeOfRange === "5D"
      ) {
        dateRangeArray = generateHourlyDateRange(
          dateRange.fromforRangeGenerator,
          dateRange.toForRangeGenerator
        );
        formatToCompare = "YYYY-MM-DD HH";
      } else {
        dateRangeArray = generateDateRange(
          dateRange.fromforRangeGenerator,
          dateRange.toForRangeGenerator
        );
      }

      const formattedVitals = vitalData.reduce<
        Dictionary<IPatientVitalGraph>[]
      >((prevValue, currentValue) => {
        const valueArray: number[] = [];
        const systolicValue: number[] = [];
        const dystolicValue: number[] = [];
        let objToPush: Dictionary<IPatientVitalGraph> = {};
        Object.keys(currentValue).map((key) => {
          objToPush = currentValue;
          dateRangeArray.forEach((date) => {
            const dateIndex = currentValue[key].created_at.findIndex(
              (dateValue) =>
                timeProvider.dateLibrary(dateValue).format(formatToCompare) ===
                timeProvider.dateLibrary(date).format(formatToCompare)
            );

            if (dateIndex !== -1) {
              if (key === "bloodPressure") {
                systolicValue.push(
                  currentValue[key].systolicValue![dateIndex]!
                    ? currentValue[key].systolicValue![dateIndex]!
                    : NaN
                );
                dystolicValue.push(
                  currentValue[key].diastolicValue![dateIndex]!
                    ? currentValue[key].diastolicValue![dateIndex]!
                    : NaN
                );
              } else {
                valueArray.push(
                  currentValue[key].value![dateIndex]!
                    ? currentValue[key].value![dateIndex]!
                    : NaN
                );
              }
            } else {
              valueArray.push(NaN);
              systolicValue.push(NaN);
              dystolicValue.push(NaN);
            }
          });

          objToPush[key].created_at = dateRangeArray;

          if (key === "bloodPressure") {
            objToPush[key].systolicValue = systolicValue;
            objToPush[key].diastolicValue = dystolicValue;
          } else {
            objToPush[key].value = valueArray;
          }

          return key;
        });

        prevValue.push(objToPush);
        return prevValue;
      }, [] as Dictionary<IPatientVitalGraph>[]);

      vital.data.data = formattedVitals;
    }

    if (
      vital.data &&
      RR.data &&
      motionBedExitData.data?.bedExit &&
      motionBedExitData.data?.motion &&
      heartRateData.data
    ) {
      const { motion, bedExit } = motionBedExitData.data;

      return {
        success: true,
        data: {
          vital: vital.data.data,
          RR: RR.data,
          motion,
          bedExit,
          HR: heartRateData.data,
        },
      };
    }

    return {
      success: false,
      error: { message: "Error" },
      data: {
        vital: vital?.data?.data || [],
        RR: RR?.data || {
          "1D": [],
          "3D": [],
          "5D": [],
          "30D": [],
          dailyExtra: [],
          hourlyExtra: [],
        },
        HR: heartRateData?.data || {
          "1D": [],
          "3D": [],
          "5D": [],
          "30D": [],
          dailyExtra: [],
          hourlyExtra: [],
        },
        bedExit: motionBedExitData?.data?.bedExit || [],
        motion: motionBedExitData?.data?.motion || [],
      },
    };
  };

  const getPatientVitalForLastVital: IGetPatientVitalForLastVital = async ({
    clinicId,
    patientId,
    endDate,
    shouldCall = true,
  }) => {
    if (!shouldCall) {
      return { success: false, error: { message: "" } };
    }
    if (!patientId) {
      // Just for safe side this should not be called when used with useSWR
      throw new Error("Patient Id null");
    }
    if (endDate) {
      endDate = timeProvider
        .dateLibrary(endDate, "YYYY-MM-DD")
        .format("YYYY-MM-DD 23:59:59");
    } else {
      endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD 23:59:59");
    }
    // Add converter here
    const [vital3D, vital30D] = await Promise.all([
      patientService.getPatientVitalGraph({
        clinicId,
        patientId,
        endDate,
        typeOfRange: "3D",
      }),
      patientService.getPatientVitalGraph({
        clinicId,
        patientId,
        endDate,
        typeOfRange: "30D",
      }),
    ]);

    const formatter = (
      vital: typeof vital3D,
      typeOfRange: "3D" | "30D",
      dateRange: {
        toForRangeGenerator: string;
        fromforRangeGenerator: string;
      }
    ) => {
      if (vital.data && vital.data.data) {
        const vitalData = vital.data.data;
        let dateRangeArray: string[] = [];
        let formatToCompare = "YYYY-MM-DD";
        if (typeOfRange === "3D") {
          dateRangeArray = generateHourlyDateRange(
            dateRange.fromforRangeGenerator,
            dateRange.toForRangeGenerator
          );
          formatToCompare = "YYYY-MM-DD HH";
        } else {
          dateRangeArray = generateDateRange(
            dateRange.fromforRangeGenerator,
            dateRange.toForRangeGenerator
          );
        }

        const formattedVitals = vitalData.reduce<
          Dictionary<IPatientVitalGraph>[]
        >((prevValue, currentValue) => {
          const valueArray: number[] = [];
          const systolicValue: number[] = [];
          const dystolicValue: number[] = [];
          let objToPush: Dictionary<IPatientVitalGraph> = {};
          Object.keys(currentValue).map((key) => {
            objToPush = currentValue;
            dateRangeArray.forEach((date) => {
              const dateIndex = currentValue[key].created_at.findIndex(
                (dateValue) =>
                  timeProvider
                    .dateLibrary(dateValue)
                    .format(formatToCompare) ===
                  timeProvider.dateLibrary(date).format(formatToCompare)
              );

              if (dateIndex !== -1) {
                if (key === "bloodPressure") {
                  systolicValue.push(
                    currentValue[key].systolicValue![dateIndex]!
                      ? currentValue[key].systolicValue![dateIndex]!
                      : NaN
                  );
                  dystolicValue.push(
                    currentValue[key].diastolicValue![dateIndex]!
                      ? currentValue[key].diastolicValue![dateIndex]!
                      : NaN
                  );
                } else {
                  valueArray.push(
                    currentValue[key].value![dateIndex]!
                      ? currentValue[key].value![dateIndex]!
                      : NaN
                  );
                }
              } else {
                valueArray.push(NaN);
                systolicValue.push(NaN);
                dystolicValue.push(NaN);
              }
            });

            objToPush[key].created_at = dateRangeArray;

            if (key === "bloodPressure") {
              objToPush[key].systolicValue = systolicValue;
              objToPush[key].diastolicValue = dystolicValue;
            } else {
              objToPush[key].value = valueArray;
            }

            return key;
          });

          prevValue.push(objToPush);
          return prevValue;
        }, [] as Dictionary<IPatientVitalGraph>[]);

        vital.data.data = formattedVitals;
        return vital;
      }
      return vital;
    };

    const parsedVital3D = formatter(vital3D, "3D", {
      toForRangeGenerator: timeProvider
        .getDateTime()
        .format("YYYY-MM-DDTHH:mm:ss"),
      fromforRangeGenerator: timeProvider
        .getDateTime()
        .subtract(3, "days")
        .format("YYYY-MM-DDT00:00:00"),
    });

    const parsedVital30D = formatter(vital30D, "30D", {
      toForRangeGenerator: timeProvider
        .getDateTime()
        .format("YYYY-MM-DDTHH:mm:ss"),
      fromforRangeGenerator: timeProvider
        .getDateTime()
        .subtract(30, "days")
        .format("YYYY-MM-DDT00:00:00"),
    });

    if (parsedVital3D.data && parsedVital30D.data) {
      return {
        success: true,
        data: {
          vital3D: vital3D?.data?.data || [],
          vital30D: vital30D?.data?.data || [],
        },
      };
    }

    return {
      success: false,
      error: { message: "Error" },
      data: {
        vital3D: vital3D?.data?.data || [],
        vital30D: vital30D?.data?.data || [],
      },
    };
  };

  const getPatientVitalForLastVitalForSBARVitalsTable: IGetPatientVitalForLastVitalForSBARVitalsTable =
    async ({ clinicId, patientId, endDate, shouldCall = true }) => {
      if (!shouldCall) {
        return { success: false, error: { message: "" } };
      }
      if (!patientId) {
        // Just for safe side this should not be called when used with useSWR
        throw new Error("Patient Id null");
      }
      if (endDate) {
        endDate = timeProvider
          .dateLibrary(endDate, "YYYY-MM-DD HH:mm:ss")
          .format("YYYY-MM-DD HH:mm:ss");
      } else {
        endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD 23:59:59");
      }
      // Add converter here
      const [vital3D] = await Promise.all([
        patientService.getPatientVitalGraph({
          clinicId,
          patientId,
          endDate,
          typeOfRange: "7D",
          hourly_format: true,
        }),
      ]);

      const formatter = (
        vital: typeof vital3D,
        typeOfRange: "7D",
        dateRange: {
          toForRangeGenerator: string;
          fromforRangeGenerator: string;
        }
      ) => {
        if (vital.data && vital.data.data) {
          const vitalData = vital.data.data;
          let dateRangeArray: string[] = [];
          let formatToCompare = "YYYY-MM-DD";
          if (typeOfRange === "7D") {
            dateRangeArray = generateHourlyDateRange(
              dateRange.fromforRangeGenerator,
              dateRange.toForRangeGenerator
            );
            formatToCompare = "YYYY-MM-DD HH";
          } else {
            dateRangeArray = generateDateRange(
              dateRange.fromforRangeGenerator,
              dateRange.toForRangeGenerator
            );
          }

          const formattedVitals = vitalData.reduce<
            Dictionary<IPatientVitalGraph>[]
          >((prevValue, currentValue) => {
            const valueArray: number[] = [];
            const systolicValue: number[] = [];
            const dystolicValue: number[] = [];
            let objToPush: Dictionary<IPatientVitalGraph> = {};
            Object.keys(currentValue).map((key) => {
              objToPush = currentValue;
              dateRangeArray.forEach((date) => {
                const dateIndex = currentValue[key].created_at.findIndex(
                  (dateValue) =>
                    timeProvider
                      .dateLibrary(dateValue)
                      .format(formatToCompare) ===
                    timeProvider.dateLibrary(date).format(formatToCompare)
                );

                if (dateIndex !== -1) {
                  if (key === "bloodPressure") {
                    systolicValue.push(
                      currentValue[key].systolicValue![dateIndex]!
                        ? currentValue[key].systolicValue![dateIndex]!
                        : NaN
                    );
                    dystolicValue.push(
                      currentValue[key].diastolicValue![dateIndex]!
                        ? currentValue[key].diastolicValue![dateIndex]!
                        : NaN
                    );
                  } else {
                    valueArray.push(
                      currentValue[key].value![dateIndex]!
                        ? currentValue[key].value![dateIndex]!
                        : NaN
                    );
                  }
                } else {
                  valueArray.push(NaN);
                  systolicValue.push(NaN);
                  dystolicValue.push(NaN);
                }
              });

              objToPush[key].created_at = dateRangeArray;

              if (key === "bloodPressure") {
                objToPush[key].systolicValue = systolicValue;
                objToPush[key].diastolicValue = dystolicValue;
              } else {
                objToPush[key].value = valueArray;
              }

              return key;
            });

            prevValue.push(objToPush);
            return prevValue;
          }, [] as Dictionary<IPatientVitalGraph>[]);

          vital.data.data = formattedVitals;
          return vital;
        }
        return vital;
      };

      const parsedVital3D = formatter(vital3D, "7D", {
        toForRangeGenerator: timeProvider
          .dateLibrary(endDate, "YYYY-MM-DD")
          .format("YYYY-MM-DD 23:59:59"),
        fromforRangeGenerator: timeProvider
          .dateLibrary(endDate, "YYYY-MM-DD")
          .subtract(7, "days")
          .format("YYYY-MM-DDT00:00:00"),
      });

      if (parsedVital3D.data) {
        return {
          success: true,
          data: {
            vital3D: vital3D?.data?.data || [],
          },
        };
      }

      return {
        success: false,
        error: { message: "Error" },
        data: {
          vital3D: vital3D?.data?.data || [],
        },
      };
    };

  const getPatientVitalGraph: IGetPatientVitalGraph = async ({
    clinicId,
    patientId,
    typeOfRange,
    shouldCall = true,
    endDate,
    hourlyFormat = false,
  }) => {
    if (!shouldCall) {
      return { success: false, error: { message: "" } };
    }
    if (!patientId) {
      // Just for safe side this should not be called when used with useSWR
      throw new Error("Patient Id null");
    }
    if (endDate) {
      endDate = timeProvider
        .dateLibrary(endDate, "YYYY-MM-DD")
        .format("YYYY-MM-DD 23:59:59");
    } else {
      endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD HH:mm:ss");

      if (["3D", "7D", "30D"].includes(typeOfRange)) {
        endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD 23:59:59");
      }
    }

    // Add converter here
    const vital = await patientService.getPatientVitalGraph({
      clinicId,
      patientId,
      endDate,
      typeOfRange,
      hourly_format: hourlyFormat,
    });
    if (vital.data) {
      return {
        success: true,
        data: vital.data.data,
      };
    }
    return {
      success: false,
      error: { message: "Error" },
      data: [],
    };
  };

  const getPatientNotes: IGetPatientNotes = async (payload) => {
    try {
      const response = await patientService.getPatientNotes(payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };
  const getPatientNotificationSettings: IGetPatientNotificationSettings =
    async (payload) => {
      try {
        const response = await patientService.getPatientNotificationSettings(
          payload
        );
        return {
          success: true,
          data: response.data,
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const getDiagnoses: IGetDiagnoses = async (payload) => {
    const diagnoses = await patientService.getDiagnoses(payload);

    if (diagnoses.data) {
      return {
        success: true,
        data: diagnoses.data.data,
      };
    }
    return {
      success: false,
      error: diagnoses.error,
    };
  };

  const saveDiagnosis: ISaveDiagnosisController = async (payload) => {
    const result = await patientService.saveDiagnosis(payload);
    if (result.success) {
      return { success: true, data: result.data };
    }
    return { success: false, error: result.error };
  };

  const getPCCNotes: IGetPCCNotes = async (payload) => {
    try {
      const response = await patientService.getPCCNotes(payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  // Monthly-window PCC notes for the MonthlyReportModal. Unwraps the
  // service's `IControllerPromiseResponse` and surfaces the raw
  // `{ data: IGetPCCNotesListItem[] }` shape — the caller (modal hook)
  // doesn't need the success/error envelope twice.
  const getPCCNotesByMonth: IGetPCCNotesByMonth = async (payload) => {
    try {
      const response = await patientService.getPCCNotesByMonth(payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientHmac: IGetPatientHmac = async (payload) => {
    try {
      const response = await patientService.getPatientHmac(payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const addPatientNote: IAddPatientNote = async ({
    clinicId,
    patientId,
    file,
    notes,
  }) => {
    const payload: Parameters<typeof patientService.addPatientNote>[0] = {
      clinicId,
      patientId,
      notes,
    };

    if (file)
      payload.file = { name: file.name, base64_enc_content: file.content };

    const { success, data, error } = await patientService.addPatientNote(
      payload
    );
    if (success && data) {
      return {
        success: true,
        data,
      };
    }
    return { success: false, error };
  };

  const getPatientRRGraphData: IGetPatientRRGraphData = async (params) => {
    if (params.isInitialLoaded) {
      return {
        success: false,
        error: { message: "Data already populated" },
      };
    }

    let startDate = timeProvider
      .getDateTime()
      .subtract(37, "days")
      .format("YYYY-MM-DDT00:00:00Z")!;
    let endDate = timeProvider.getDateTime().format("YYYY-MM-DDT23:59:59Z")!;

    if (params.endDate) {
      endDate = timeProvider
        .convertIntoLocalDateTime(params.endDate, "YYYY-MM-DD HH:mm:ss")
        ?.add(1, "hour")
        ?.format("YYYY-MM-DDTHH:mm:ssZ") as string;

      startDate = timeProvider
        .convertIntoLocalDateTime(params.endDate, "YYYY-MM-DD HH:mm:ss")
        ?.subtract(37, "days")
        ?.format("YYYY-MM-DDTHH:mm:ssZ") as string;
    }
    const response = await cdlService.getPatientGraphData({
      patient_id: params.patientId,
      dateRange: {
        from: startDate,
        to: endDate,
      },
    });
    return response;
  };

  const getPatientMotionBedExitData: IGetPatientMotionBedExitData = async ({
    patientId,
    dateRange,
    isInitialLoaded,
  }) => {
    if (!patientId) {
      return {
        success: false,
        error: { message: "Patient ID is required" },
      };
    }
    if (isInitialLoaded) {
      return {
        success: false,
        error: { message: "Data already populated" },
      };
    }

    const response = await cdlService.getBedExitMotionData({
      patient_id: patientId,
      dateRange,
    });
    return response;
  };

  const getPatientDailyRRData: IGetPatientDailyRRData = async (params) => {
    const response = await cdlService.getPatientDailyRRData({
      patient_id: params.patientId,
      dateRange: params.dateRange,
    });

    return response;
  };

  const getPatientDailyHRData: IGetPatientDailyHRDataController = async (
    params
  ) => {
    const response = await cdlService.getPatientDailyHRData({
      patient_id: params.patientId,
      dateRange: params.dateRange,
    });

    return response;
  };

  const getPatientHourlyRRData: IGetPatientDailyRRData = async (params) => {
    const response = await cdlService.getPatientHourlyRRData({
      patient_id: params.patientId,
      dateRange: params.dateRange,
    });
    return response;
  };

  const getPatientHourlyHRData: IGetPatientDailyHRDataController = async (
    params
  ) => {
    const response = await cdlService.getPatientHourlyHRData({
      patient_id: params.patientId,
      dateRange: params.dateRange,
    });
    return response;
  };

  // Trailing 5-day window for the gated minutely "1D" view. The chart defaults
  // to the most recent 24h and lets the user scroll back across the full range.
  const getMinutelyDateRange = () => ({
    from: timeProvider
      .getDateTime()
      .subtract(5, "days")
      .format("YYYY-MM-DDTHH:mm:ssZ")!,
    to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ")!,
  });

  const getPatientMinutelyRRData: IGetPatientMinutelyVitalData = async (
    params
  ) => {
    const response = await cdlService.getPatientMinutelyRRData({
      patient_id: params.patientId,
      dateRange: getMinutelyDateRange(),
    });
    return response;
  };

  const getPatientMinutelyHRData: IGetPatientMinutelyVitalData = async (
    params
  ) => {
    const response = await cdlService.getPatientMinutelyHRData({
      patient_id: params.patientId,
      dateRange: getMinutelyDateRange(),
    });
    return response;
  };

  const getPatientsLatestVitals: IGetPatientsLatestVitals = async ({
    patientIds,
    metric,
    granularity,
    signal,
    onChunk,
  }) => {
    const now = timeProvider.getDateTime();
    const dateRange =
      granularity === "hourly"
        ? {
            from: now.subtract(168, "hours").format("YYYY-MM-DDTHH:mm:ssZ"),
            to: now.format("YYYY-MM-DDTHH:mm:ssZ"),
          }
        : {
            from: now.subtract(37, "days").format("YYYY-MM-DDT00:00:00Z"),
            to: now.format("YYYY-MM-DDT23:59:59Z"),
          };

    return cdlService.getLatestVitalsForPatients({
      patientIds,
      metric,
      granularity,
      dateRange,
      signal,
      onChunk,
    });
  };

  const getPatientHeartRateData: IGetPatientRRGraphData = async (params) => {
    if (params.isInitialLoaded) {
      return {
        success: false,
        error: { message: "Data already populated" },
      };
    }

    let startDate = timeProvider
      .getDateTime()
      .subtract(37, "days")
      .format("YYYY-MM-DDT00:00:00Z")!;
    let endDate = timeProvider.getDateTime().format("YYYY-MM-DDT23:59:59Z")!;

    if (params.endDate) {
      endDate = timeProvider
        .convertIntoLocalDateTime(params.endDate, "YYYY-MM-DD HH:mm:ss")
        ?.add(1, "hour")
        ?.format("YYYY-MM-DDTHH:mm:ssZ") as string;

      startDate = timeProvider
        .convertIntoLocalDateTime(params.endDate, "YYYY-MM-DD HH:mm:ss")
        ?.subtract(37, "days")
        ?.format("YYYY-MM-DDTHH:mm:ssZ") as string;
    }

    const response = await cdlService.getHeartRateData({
      patient_id: params.patientId,
      dateRange: {
        from: startDate,
        to: endDate,
      },
    });
    return response;
  };

  const getPatientListDashboard: IPatientListDashboard = async ({
    clinicId,
    floorIds,
    no_of_days,
  }) => {
    try {
      const response = await patientService.getPatientListDashboard({
        clinic_id: clinicId,
        floorIds,
        no_of_days: no_of_days || 5,
      });

      return {
        success: true,
        data: response.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  return {
    getPatientSummaryProfile,
    getPatientBillingInfo,
    getPatientList: patientService.getPatientList,
    getPatientEventList: patientService.getPatientEventList,
    getPatientPanelProfile,
    getPatientVitalAndRRGraph,
    getPatientNotes,
    getPatientNotificationSettings,
    getDiagnoses,
    saveDiagnosis,
    getPCCNotes,
    getPCCNotesByMonth,
    getPatientHmac,
    addPatientNote,
    getPatientRRGraphData,
    getPatientVitalGraph,
    getCircadiaIQ,
    getOrderIQ,
    getCircadiaIQForRecertification,
    generateCircadiaIQ,
    getPatientVitalForLastVital,
    getPatientHeartRateData,
    getPatientMotionBedExitData,
    getPatientListDashboard,
    generateDateRange,
    getPatientDailyRRData,
    getPatientDailyHRData,
    getPatientHourlyRRData,
    getPatientHourlyHRData,
    getPatientMinutelyRRData,
    getPatientMinutelyHRData,
    getPatientsLatestVitals,
    registerPatientDevice: patientService.registerPatientDevice,
    assignPatientDevice: patientService.assignPatientDevice,
    removePatientDevice: patientService.removePatientDevice,
    getPatientAssignedDevice: patientService.getPatientAssignedDevice,
    getPatientDeviceConnectionStatus:
      patientService.getPatientDeviceConnectionStatus,
    getVnsPatientAdmissionCohort: patientService.getVnsPatientAdmissionCohort,
    updateVnsPatientAdmissionCohort:
      patientService.updateVnsPatientAdmissionCohort,

    getPatientVitalForLastVitalForSBARVitalsTable,
  };
};
